import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { generateDPATemplate } from "@/services/dpdpApi";

const DATA_CATEGORIES = [
  { value: "aadhaar", label: "Aadhaar / Government ID" },
  { value: "financial", label: "Financial / Banking Data" },
  { value: "health", label: "Health / Medical Records" },
  { value: "biometric", label: "Biometric Data" },
  { value: "precise_location", label: "Precise Location" },
  { value: "name", label: "Name / Full Name" },
  { value: "email", label: "Email Address" },
  { value: "phone", label: "Phone Number" },
  { value: "address", label: "Home Address" },
  { value: "date_of_birth", label: "Date of Birth" },
  { value: "employment", label: "Employment Information" },
  { value: "education", label: "Education Records" },
  { value: "government_id", label: "Other Government ID" },
];

export default function DPATemplatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    vendor_name: "",
    data_fiduciary_name: "Right Doers World",
    dpo_name: user?.name || "",
    data_categories: [],
    processing_purposes: "",
    sub_processors_allowed: false,
    retention_days: 730,
  });

  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(false);

  const toggleCategory = (val) => {
    setForm((f) => ({
      ...f,
      data_categories: f.data_categories.includes(val)
        ? f.data_categories.filter((c) => c !== val)
        : [...f.data_categories, val],
    }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.vendor_name || form.data_categories.length === 0 || !form.processing_purposes) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      setGenerating(true);
      const result = await generateDPATemplate(form);
      setGenerated(result);
      toast.success("DPA document generated");
    } catch (err) {
      toast.error("Failed to generate DPA template");
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    if (!generated?.html) return;
    const w = window.open("", "_blank");
    w.document.write(generated.html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <span>📄</span> DPA Template Generator
            </h1>
            <p className="text-slate-400 text-sm mt-0.5">
              Generate a DPDP Act 2023 compliant Data Processing Agreement
            </p>
          </div>
          <Button
            variant="outline"
            className="border-indigo-500 text-indigo-400 hover:bg-indigo-950"
            onClick={() => navigate("/admin/vendors")}
          >
            ← Vendor Registry
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Info banner */}
        <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-4">
          <p className="text-indigo-300 text-sm">
            <strong>Legal basis:</strong> This template is generated pursuant to <strong>Section 8(5)</strong> of the
            Digital Personal Data Protection Act, 2023. It includes all mandatory clauses: data security obligations,
            72-hour breach notification, data erasure within 30 days of termination, audit rights, and
            sub-processor restrictions. Print the document and obtain signatures from both parties.
          </p>
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">DPA Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerate} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-300">Data Fiduciary (Your Organisation) *</label>
                  <Input
                    value={form.data_fiduciary_name}
                    onChange={(e) => setForm((f) => ({ ...f, data_fiduciary_name: e.target.value }))}
                    placeholder="Right Doers World"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-300">DPO Signatory Name *</label>
                  <Input
                    value={form.dpo_name}
                    onChange={(e) => setForm((f) => ({ ...f, dpo_name: e.target.value }))}
                    placeholder="Jane Smith"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-300">Data Processor (Vendor) Name *</label>
                  <Input
                    value={form.vendor_name}
                    onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))}
                    placeholder="Stripe Inc."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-300">Retention Period (days)</label>
                  <Input
                    type="number"
                    min="1"
                    max="3650"
                    value={form.retention_days}
                    onChange={(e) => setForm((f) => ({ ...f, retention_days: parseInt(e.target.value) || 365 }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">
                  Data Categories Processed by Vendor *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {DATA_CATEGORIES.map((cat) => {
                    const selected = form.data_categories.includes(cat.value);
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => toggleCategory(cat.value)}
                        className={`px-3 py-2 text-sm border rounded cursor-pointer text-left transition-colors ${
                          selected
                            ? "bg-indigo-900/50 border-indigo-500 text-indigo-200 font-medium"
                            : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
                        }`}
                      >
                        {selected ? "✓ " : ""}{cat.label}
                      </button>
                    );
                  })}
                </div>
                {form.data_categories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {form.data_categories.map((c) => (
                      <Badge key={c} variant="outline" className="bg-indigo-950/50 text-indigo-300 border-indigo-500/50 text-xs">
                        {c}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-300">
                  Processing Purposes *
                  <span className="text-xs text-slate-500 ml-2">(describe what the vendor does with this data)</span>
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2 bg-background text-white min-h-[100px]"
                  value={form.processing_purposes}
                  onChange={(e) => setForm((f) => ({ ...f, processing_purposes: e.target.value }))}
                  placeholder="Describe the specific data processing activities, purposes, and technical measures implemented by the vendor..."
                  required
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="sub-processors"
                  checked={form.sub_processors_allowed}
                  onChange={(e) => setForm((f) => ({ ...f, sub_processors_allowed: e.target.checked }))}
                  className="w-4 h-4 accent-indigo-500"
                />
                <label htmlFor="sub-processors" className="text-sm text-slate-300">
                  Allow sub-processors with prior written consent
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Generating...
                    </>
                  ) : "Generate DPA Document"}
                </Button>
                {generated && (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    onClick={() => setPreview(!preview)}
                  >
                    {preview ? "Hide Preview" : "Preview HTML"}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Preview */}
        {generated && preview && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">DPA Document Preview</CardTitle>
                <Button className="bg-green-600 hover:bg-green-700" onClick={handlePrint}>
                  🖨 Print / Save as PDF
                </Button>
              </div>
              <p className="text-slate-400 text-sm">
                Use your browser's Print function (Ctrl/Cmd+P) and select "Save as PDF" to save.
              </p>
            </CardHeader>
            <CardContent>
              <div
                dangerouslySetInnerHTML={{ __html: generated.html }}
                className="prose prose-sm max-w-none"
                style={{
                  background: "white",
                  color: "#1a1a1a",
                  padding: "40px",
                  borderRadius: "8px",
                  maxHeight: "600px",
                  overflow: "auto",
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* Generated success */}
        {generated && !preview && (
          <Card className="bg-green-900/20 border-green-500/30">
            <CardContent className="py-6 flex items-center gap-4">
              <span className="text-3xl">✅</span>
              <div className="flex-1">
                <p className="text-green-300 font-semibold">DPA generated for: {generated.vendor_name}</p>
                <p className="text-green-400 text-sm">Generated at: {new Date(generated.generated_at).toLocaleString()}</p>
              </div>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handlePrint}>
                🖨 Print / Save as PDF
              </Button>
              <Button variant="outline" className="border-green-500 text-green-400"
                onClick={() => setPreview(true)}>
                Preview HTML
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
